import {
  boundedMessages,
  boundedNodeLabel,
  boundedTimestamp,
  invalid,
  isRecord,
  requiredPositiveInteger,
  requiredString,
  validateAttributeKeys,
  validateExactKeys,
} from "./kratos-contract-primitives.js";

const IMAGE_ATTRIBUTE_KEYS = new Set(["height", "id", "node_type", "src", "width"]);
const TEXT_ATTRIBUTE_KEYS = new Set(["id", "node_type", "text"]);
const ANCHOR_ATTRIBUTE_KEYS = new Set(["href", "id", "node_type", "title"]);
const COMPLETION_CSRF_ATTRIBUTE_KEYS = new Set([
  "disabled",
  "name",
  "node_type",
  "required",
  "type",
  "value",
]);
const MAX_TOTP_QR_BYTES = 256 * 1024;
const MAX_LOOKUP_SECRETS = 16;

function boundedTextMessage(value, label, expectedId) {
  validateExactKeys(value, new Set(["context", "id", "text", "type"]), label);
  const id = requiredPositiveInteger(value.id, `${label} ID`, { max: 9_999_999 });
  if (id !== expectedId || value.type !== "info") {
    invalid(`${label} is unsupported`, "unsupported");
  }
  return {
    id,
    type: "info",
    text: requiredString(value.text, `${label} text`, { maxLength: 4096 }),
    context: value.context,
  };
}

function decodeTOTPTextMessage(value, label) {
  const message = boundedTextMessage(value, label, 1050006);
  validateExactKeys(message.context, new Set(["secret"]), `${label} context`);
  const secret = requiredString(message.context.secret, `${label} secret`, { maxLength: 256 });
  if (message.text !== secret) {
    invalid(`${label} secret projection is inconsistent`);
  }
  return Object.freeze({
    id: message.id,
    type: message.type,
    text: message.text,
    context: Object.freeze({ secret }),
  });
}

function decodeLookupCodeMessage(value, label) {
  validateExactKeys(value, new Set(["context", "id", "text", "type"]), label);
  const id = requiredPositiveInteger(value.id, `${label} ID`, { max: 9_999_999 });
  if (value.type !== "info") {
    invalid(`${label} type is unsupported`, "unsupported");
  }
  const text = requiredString(value.text, `${label} text`, { maxLength: 256 });
  if (id === 1050009) {
    validateExactKeys(value.context, new Set(["secret"]), `${label} context`);
    const code = requiredString(value.context.secret, `${label} secret`, { maxLength: 128 });
    if (text !== code) {
      invalid(`${label} secret projection is inconsistent`);
    }
    return Object.freeze({ kind: "available", code });
  }
  if (id === 1050014) {
    validateExactKeys(
      value.context,
      new Set(["used_at", "used_at_unix"]),
      `${label} context`,
    );
    const usedAt = boundedTimestamp(value.context.used_at, `${label} used time`);
    if (
      !Number.isSafeInteger(value.context.used_at_unix)
      || Math.trunc(Date.parse(usedAt) / 1000) !== value.context.used_at_unix
    ) {
      invalid(`${label} used timestamp is inconsistent`);
    }
    return Object.freeze({ kind: "used", usedAt });
  }
  invalid(`${label} ID is unsupported`, "unsupported");
}

function decodeLookupTextMessage(value, label) {
  const message = boundedTextMessage(value, label, 1050015);
  validateExactKeys(message.context, new Set(["secrets"]), `${label} context`);
  if (
    !Array.isArray(message.context.secrets)
    || message.context.secrets.length === 0
    || message.context.secrets.length > MAX_LOOKUP_SECRETS
  ) {
    invalid(`${label} backup codes are invalid`);
  }
  const codes = Object.freeze(message.context.secrets.map((code, index) => (
    decodeLookupCodeMessage(code, `${label} backup code ${index}`)
  )));
  return Object.freeze({
    id: message.id,
    type: message.type,
    text: message.text,
    context: Object.freeze({ codes }),
  });
}

export function decodeSecurityImageNode(node, index, view) {
  const label = `${view} UI node ${index}`;
  if (!isRecord(node) || node.type !== "img" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  const attributes = node.attributes;
  validateAttributeKeys(attributes, IMAGE_ATTRIBUTE_KEYS, label);
  const id = requiredString(attributes.id, `${label} ID`, { maxLength: 64 });
  const width = requiredPositiveInteger(attributes.width, `${label} width`, { max: 512 });
  const height = requiredPositiveInteger(attributes.height, `${label} height`, { max: 512 });
  const src = requiredString(attributes.src, `${label} source`, {
    maxLength: Math.ceil(MAX_TOTP_QR_BYTES / 3) * 4 + 64,
  });
  const prefix = "data:image/png;base64,";
  const payload = src.startsWith(prefix) ? src.slice(prefix.length) : "";
  if (
    view !== "settings"
    || node.group !== "totp"
    || attributes.node_type !== "img"
    || id !== "totp_qr"
    || payload.length === 0
    || payload.length % 4 !== 0
    || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload)
  ) {
    invalid(`${label} TOTP image is unsupported`, "unsupported");
  }
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  const decodedBytes = (payload.length / 4) * 3 - padding;
  if (decodedBytes <= 0 || decodedBytes > MAX_TOTP_QR_BYTES) {
    invalid(`${label} TOTP image exceeds its byte bound`, "unsupported");
  }
  return Object.freeze({
    kind: "image",
    group: "totp",
    id,
    src,
    width,
    height,
    label: boundedNodeLabel(node.meta, label),
    messages: boundedMessages(node.messages, `${label} messages`),
  });
}

export function decodeSecurityTextNode(node, index, view) {
  const label = `${view} UI node ${index}`;
  if (!isRecord(node) || node.type !== "text" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  const attributes = node.attributes;
  validateAttributeKeys(attributes, TEXT_ATTRIBUTE_KEYS, label);
  const id = requiredString(attributes.id, `${label} ID`, { maxLength: 64 });
  if (view !== "settings" || attributes.node_type !== "text") {
    invalid(`${label} text is unsupported`, "unsupported");
  }
  let text;
  if (node.group === "totp" && id === "totp_secret_key") {
    text = decodeTOTPTextMessage(attributes.text, `${label} text`);
  } else if (node.group === "lookup_secret" && id === "lookup_secret_codes") {
    text = decodeLookupTextMessage(attributes.text, `${label} text`);
  } else {
    invalid(`${label} text is unsupported`, "unsupported");
  }
  return Object.freeze({
    kind: "text",
    group: node.group,
    id,
    text,
    label: boundedNodeLabel(node.meta, label),
    messages: boundedMessages(node.messages, `${label} messages`),
  });
}

function decodeContinueAnchor(node, index, context) {
  const label = `verification UI node ${index}`;
  if (!isRecord(node) || node.type !== "a" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  validateAttributeKeys(node.attributes, ANCHOR_ATTRIBUTE_KEYS, label);
  const attributes = node.attributes;
  if (
    node.group !== "code"
    || attributes.node_type !== "a"
    || attributes.id !== "continue"
    || attributes.href !== context.returnUrl
  ) {
    invalid(`${label} continue link is outside the exact account contract`, "unsupported");
  }
  validateExactKeys(attributes.title, new Set(["id", "text", "type"]), `${label} title`);
  if (attributes.title.id !== 1070009 || attributes.title.type !== "info") {
    invalid(`${label} title is unsupported`, "unsupported");
  }
  const title = Object.freeze({
    id: 1070009,
    type: "info",
    text: requiredString(attributes.title.text, `${label} title text`, { maxLength: 100 }),
  });
  validateExactKeys(node.meta ?? {}, new Set(["label"]), `${label} metadata`);
  if (node.meta?.label !== undefined) {
    validateExactKeys(node.meta.label, new Set(["id", "text", "type"]), `${label} label`);
    if (
      node.meta.label.id !== title.id
      || node.meta.label.type !== title.type
      || node.meta.label.text !== title.text
    ) {
      invalid(`${label} label is inconsistent`);
    }
  }
  if (boundedMessages(node.messages, `${label} messages`).length !== 0) {
    invalid(`${label} messages are unsupported`, "unsupported");
  }
  return Object.freeze({
    kind: "anchor",
    group: "code",
    id: "continue",
    href: context.returnUrl,
    title,
  });
}

function validateCompletionCsrf(node, index) {
  const label = `verification UI node ${index}`;
  if (!isRecord(node) || node.type !== "input" || !isRecord(node.attributes)) {
    invalid(`${label} is unsupported`, "unsupported");
  }
  validateExactKeys(node.attributes, COMPLETION_CSRF_ATTRIBUTE_KEYS, `${label} attributes`);
  if (
    node.group !== "default"
    || node.attributes.node_type !== "input"
    || node.attributes.name !== "csrf_token"
    || node.attributes.type !== "hidden"
    || node.attributes.required !== true
    || node.attributes.disabled !== false
  ) {
    invalid(`${label} completion CSRF control is unsupported`, "unsupported");
  }
  requiredString(node.attributes.value, `${label} CSRF value`, { maxLength: 4096 });
  validateExactKeys(node.meta ?? {}, new Set(), `${label} metadata`);
  if (boundedMessages(node.messages, `${label} messages`).length !== 0) {
    invalid(`${label} messages are unsupported`, "unsupported");
  }
}

export function decodeVerificationCompletion(value, context, id, expiresAt) {
  if (value.ui.nodes.length !== 2) {
    invalid("Completed verification flow has an unsupported UI node count", "unsupported");
  }
  if (
    String(value.ui.method).toUpperCase() !== "GET"
    || value.ui.action !== context.returnUrl
  ) {
    invalid("Completed verification action is outside the localized account page");
  }
  validateCompletionCsrf(value.ui.nodes[0], 0);
  return Object.freeze({
    view: "verification",
    id,
    action: context.returnUrl,
    expiresAt,
    refresh: false,
    requestedAal: "",
    forms: Object.freeze([]),
    completion: Object.freeze({
      kind: "verificationPassed",
      continueNode: decodeContinueAnchor(value.ui.nodes[1], 1, context),
    }),
    webauthnScript: null,
    passkeyOnloadTrigger: "",
    messages: boundedMessages(value.ui.messages, "verification flow messages"),
  });
}
